import * as Sentry from '@sentry/react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import i18n from 'i18next';
import type {
  AgentChatErrorState,
  AgentChatState,
  AgentErrorCode,
  CardBusyReason,
  ChatBubble,
  ConfirmationBatchResult,
  ConfirmationBatchState,
  PendingConfirmation,
  ResumableThread,
} from './types';
import { isBubbleSkippedWidgetType } from '@hero/agent';
import { coerceWidgetSpec } from './bubbleBlocks';

/**
 * Severity per `AgentErrorCode` for browser-side Sentry capture.
 *
 * The backend already reports its own view of the failure (with the
 * real stack), but capturing on the client too gives us the browser
 * session context — release, URL, language, breadcrumbs — and
 * catches SSE-layer failures (network drops, malformed frames) that
 * never reach the backend `AgentStreamerService` catch block.
 *
 * `cancelled` is intentionally omitted: the user aborted, this is
 * not an incident.
 */
const AGENT_ERROR_SEVERITY: Partial<
  Record<AgentErrorCode, Sentry.SeverityLevel>
> = {
  configuration: 'fatal',
  unknown: 'error',
  timeout: 'warning',
  rate_limited: 'warning',
  unauthorized: 'info',
};

/**
 * Pick the label that matches the active language from a
 * `status_update` event. Falls back across `ur → en` and `ar → en`
 * so any missed translation degrades to English instead of an empty
 * subline.
 */
const pickStatusLabel = (event: {
  labelEn: string;
  labelAr: string;
  labelUr: string;
}): string => {
  const lang = (i18n.language ?? 'en').toLowerCase();
  if (lang.startsWith('ar')) return event.labelAr || event.labelEn;
  if (lang.startsWith('ur')) return event.labelUr || event.labelEn;
  return event.labelEn;
};

/**
 * Single source of truth for the chat panel.
 *
 * Why Zustand (not React state, not Redux): the chat panel is open
 * across page navigations (the Layout mounts it), and the Composer
 * + MessageList live in different parts of the tree. A small
 * Zustand store gives us a flat, fast subscription model without
 * the redux boilerplate this monorepo intentionally avoids.
 *
 * The store deliberately doesn't talk to the network — the manager
 * hook (`useAgentChatManager`) wires the SSE stream from
 * `useAgentSendMessageStream` into store actions. This keeps the
 * store reusable across surfaces (the `/ai` full-page route, an
 * embedded widget, a future iframe), and makes manual testing
 * trivial.
 */
export interface AgentChatActions {
  /** Open / close / toggle the panel. */
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setExpanded: (expanded: boolean) => void;

  /** Start a fresh user turn. Appends a user bubble + a placeholder assistant. */
  startUserTurn: (text: string) => { userBubbleId: string; assistantBubbleId: string };

  /** Apply an SSE event to the store. */
  applyEvent: (event: import('./types').AgentChatEvent) => void;

  /**
   * Offer cards the server still holds open from an earlier page load.
   * Parked in `resumable` rather than pushed into the thread, so the
   * panel keeps its empty state (and its prompt chips) until the user
   * asks for them. Ignored once the panel holds a live thread.
   */
  offerResume: (input: ResumableThread) => void;

  /** Take up the offer — the parked cards become live pending cards. */
  acceptResume: () => void;

  /** Drop the offer (user discarded the drafts, or answered them). */
  dismissResume: () => void;

  /** Append a confirmation outcome bubble (post `confirmMessage`). */
  appendAssistantBubble: (bubble: ChatBubble) => void;

  /**
   * Clear pending confirmation cards.
   *
   * - With `confirmationId` → removes only that card (the rest of the
   *   batch keeps awaiting the user). Status flips to `idle` only once
   *   the list drains to empty.
   * - Without `confirmationId` → clears the whole pending batch (used
   *   on `reset()` / sign-out / cancel).
   */
  clearPendingConfirmation: (confirmationId?: string) => void;

  /**
   * Merge edits from the confirmation card identified by
   * `confirmationId` into its staged payload.
   */
  updatePendingConfirmationPayload: (
    confirmationId: string,
    partial: Record<string, unknown>,
  ) => void;

  /**
   * Deep-update a nested path on the confirmation card identified by
   * `confirmationId` (e.g. `destinations[1].price`).
   */
  updatePendingConfirmationPayloadAtPath: (
    confirmationId: string,
    path: (string | number)[],
    value: unknown,
  ) => void;

  /**
   * Flag / clear async work on a card (see {@link AgentChatState.busyCards}).
   * Reasons are tracked individually so a price quote finishing while
   * the location picker is still open doesn't unlock Confirm.
   */
  setCardBusy: (
    confirmationId: string,
    reason: CardBusyReason,
    busy: boolean,
  ) => void;

  /** Begin a bulk confirm / decline batch. Locks the stack UI. */
  startConfirmationBatch: (input: Omit<ConfirmationBatchState, 'results' | 'completed'>) => void;

  /** Advance batch progress + optionally record a finished card result. */
  advanceConfirmationBatch: (input: {
    completed: number;
    currentConfirmationId?: string;
    currentClientName?: string;
    result?: ConfirmationBatchResult;
  }) => void;

  /** Clear batch lock after bulk operation finishes. */
  finishConfirmationBatch: () => void;

  /** Mark a single-card confirm as in-flight. */
  setResolvingConfirmationId: (confirmationId?: string) => void;

  /** Patch an existing bubble (batch progress → outcome). */
  updateBubble: (bubbleId: string, patch: Partial<ChatBubble>) => void;

  /** Upsert a batch-progress activity chip on the progress bubble. */
  upsertBatchProgressActivity: (input: {
    bubbleId: string;
    activityId: string;
    label: string;
    status: 'running' | 'ok' | 'failed';
    errorMessage?: string;
  }) => void;

  /** Reset the panel to its initial state (sign-out, cancellation). */
  reset: () => void;
}

const initialState: AgentChatState = {
  open: false,
  expanded: false,
  status: 'idle',
  bubbles: [],
  pendingConfirmations: [],
  isMultiConfirmationStack: false,
  busyCards: {},
};

let bubbleCounter = 0;
const nextBubbleId = () => `bubble-${Date.now()}-${++bubbleCounter}`;

export const useAgentChatStore = create<AgentChatState & AgentChatActions>()(
  immer(set => ({
    ...initialState,

    openPanel: () => set(s => void (s.open = true)),
    closePanel: () => set(s => void (s.open = false)),
    togglePanel: () => set(s => void (s.open = !s.open)),
    setExpanded: expanded => set(s => void (s.expanded = expanded)),

    startUserTurn: (text: string) => {
      const userBubbleId = nextBubbleId();
      const assistantBubbleId = nextBubbleId();
      set(s => {
        s.bubbles.push({ id: userBubbleId, role: 'user', text, widgets: [] });
        s.bubbles.push({
          id: assistantBubbleId,
          role: 'assistant',
          text: '',
          widgets: [],
          streaming: true,
        });
        s.status = 'streaming';
        s.error = undefined;
      });
      return { userBubbleId, assistantBubbleId };
    },

    offerResume: ({ conversationId, confirmations }) =>
      set(s => {
        if (
          confirmations.length === 0 ||
          s.bubbles.length > 0 ||
          s.pendingConfirmations.length > 0 ||
          s.status === 'streaming' ||
          s.confirmationBatch
        ) {
          return;
        }
        s.resumable = { conversationId, confirmations };
      }),

    acceptResume: () =>
      set(s => {
        const offer = s.resumable;
        if (!offer) return;
        s.conversationId = offer.conversationId;
        s.pendingConfirmations = offer.confirmations;
        s.isMultiConfirmationStack = offer.confirmations.length >= 2;
        s.status = 'awaiting_confirmation';
        s.resumable = undefined;
      }),

    dismissResume: () => set(s => void (s.resumable = undefined)),

    applyEvent: event =>
      set(s => {
        const lastAssistant = lastAssistantBubble(s.bubbles);
        switch (event.type) {
          case 'run_started': {
            s.conversationId = event.conversationId;
            break;
          }
          case 'status_update': {
            if (!lastAssistant) break;
            // Don't downgrade once real prose has started — the
            // streamer can race `composing` past the first token if
            // the tool replies fast enough. Empty text == still
            // pre-prose, so it's safe to keep updating the subline.
            if (lastAssistant.text.length === 0) {
              lastAssistant.statusPhase = event.phase;
              lastAssistant.statusLabel = pickStatusLabel(event);
            }
            break;
          }
          case 'assistant_token': {
            if (lastAssistant) {
              lastAssistant.text += event.delta;
              lastAssistant.streaming = true;
              // First token wins — drop any in-flight status subline.
              if (lastAssistant.statusLabel) {
                lastAssistant.statusLabel = undefined;
                lastAssistant.statusPhase = undefined;
              }
            }
            break;
          }
          case 'tool_started': {
            if (!lastAssistant) break;
            if (!lastAssistant.activities) lastAssistant.activities = [];
            const id = chipId(event.runId, event.toolCallId);
            const existing = lastAssistant.activities.find(a => a.id === id);
            if (existing) {
              existing.status = 'running';
            } else {
              lastAssistant.activities.push({
                id,
                runId: event.runId,
                name: event.name,
                status: 'running',
              });
            }
            break;
          }
          case 'tool_finished': {
            if (!lastAssistant?.activities) break;
            const id = chipId(event.runId, event.toolCallId);
            const chip = lastAssistant.activities.find(a => a.id === id);
            if (chip) {
              chip.status = event.ok ? 'ok' : 'failed';
              chip.errorMessage = event.errorMessage;
            }
            break;
          }
          case 'assistant_widget': {
            if (!lastAssistant) break;
            const spec = coerceWidgetSpec(event.widget);
            if (spec && !isBubbleSkippedWidgetType(spec.type)) {
              lastAssistant.widgets.push(spec);
            }
            break;
          }
          case 'confirmation_required': {
            const next: PendingConfirmation = {
              id: event.confirmationId,
              action: event.action,
              payload: event.payload,
            };
            const existingIdx = s.pendingConfirmations.findIndex(
              p => p.id === event.confirmationId,
            );
            if (existingIdx >= 0) {
              // Re-stage of the same toolCallId (rare — happens if the
              // LLM rebuilds the card mid-turn). Replace in place so
              // the user's edits to siblings aren't reordered.
              s.pendingConfirmations[existingIdx] = next;
            } else {
              s.pendingConfirmations.push(next);
            }
            if (s.pendingConfirmations.length >= 2) {
              s.isMultiConfirmationStack = true;
            }
            // The live turn owns this card now — drop it from the
            // resume offer so it can't render twice.
            if (s.resumable) {
              s.resumable.confirmations = s.resumable.confirmations.filter(
                c => c.id !== event.confirmationId,
              );
              if (s.resumable.confirmations.length === 0) {
                s.resumable = undefined;
              }
            }
            s.status = 'awaiting_confirmation';
            break;
          }
          case 'assistant_message_committed': {
            if (lastAssistant) {
              lastAssistant.streaming = false;
              lastAssistant.statusLabel = undefined;
              lastAssistant.statusPhase = undefined;
              const committedText = textFromCommittedBlocks(event.blocks);
              if (committedText) {
                lastAssistant.text = committedText;
              }
              mergeWidgetsFromBlocks(lastAssistant, event.blocks);
              if (event.conversationMessageId) {
                lastAssistant.id = event.conversationMessageId;
              }
            }
            // Mid-turn flush (fast-ack / identity-first): park that
            // bubble and open a new streaming slot so later tokens are
            // not glued onto the first sentence.
            if (event.immediate) {
              s.bubbles.push({
                id: nextBubbleId(),
                role: 'assistant',
                text: '',
                widgets: [],
                streaming: true,
              });
              s.status = 'streaming';
            }
            break;
          }
          case 'run_finished': {
            if (lastAssistant) {
              lastAssistant.streaming = false;
              lastAssistant.statusLabel = undefined;
              lastAssistant.statusPhase = undefined;
            }
            const trailing = s.bubbles[s.bubbles.length - 1];
            if (
              trailing &&
              trailing.role === 'assistant' &&
              !trailing.text &&
              (trailing.widgets?.length ?? 0) === 0 &&
              (trailing.activities?.length ?? 0) === 0 &&
              !trailing.errorCode
            ) {
              s.bubbles.pop();
            }
            // If the turn staged any confirmation cards we stay in
            // `awaiting_confirmation` until they drain; otherwise we
            // settle on `idle` / `error` per the run result.
            if (s.pendingConfirmations.length === 0) {
              s.status = event.status === 'completed' ? 'idle' : 'error';
            } else {
              s.status = 'awaiting_confirmation';
            }
            break;
          }
          case 'error': {
            // Backend always sends a sanitized `message` and a stable
            // `code` (older builds may only send `message` — fall back
            // to `'unknown'`). We NEVER render `event.message` raw —
            // it's just a fallback for the localized `errors.<code>`.
            const rawCode = (event as { code?: AgentErrorCode }).code;
            const code: AgentErrorCode = rawCode ?? 'unknown';
            const referenceId = (event as { referenceId?: string })
              .referenceId;

            const errorState: AgentChatErrorState = {
              code,
              fallback: event.message,
              referenceId,
            };
            s.status = 'error';
            s.error = errorState;

            // Console logging lives in the SDK (`logAgentStreamEventError`,
            // gated by `enableLogs`). The backend also captures a Sentry
            // event with the real stack. We ALSO capture on the client
            // for two reasons: (1) SSE-layer failures (dropped
            // connection, malformed frame) never reach the backend
            // `AgentStreamerService` catch block; (2) the browser event
            // carries session context — release, URL, user language,
            // navigation breadcrumbs — that the backend event doesn't
            // have. `fingerprint` groups every user's report of the
            // same failure into a single Sentry issue so one alert
            // catches everyone. `cancelled` is a user action, not an
            // incident — skipped.
            const severity = AGENT_ERROR_SEVERITY[code];
            if (severity) {
              Sentry.captureException(
                new Error(`Hero AI SSE error: ${code}`),
                {
                  level: severity,
                  tags: {
                    feature: 'agent',
                    surface: 'chatPanel',
                    errorCode: code,
                  },
                  contexts: {
                    agent: {
                      code,
                      backendReferenceId: referenceId ?? null,
                      fallbackMessage: event.message,
                    },
                  },
                  fingerprint: ['hero-ai', 'sse-error', code],
                },
              );
            }

            if (lastAssistant) {
              lastAssistant.streaming = false;
              lastAssistant.statusLabel = undefined;
              lastAssistant.statusPhase = undefined;
              // Tag the bubble so `MessageList` can swap in a
              // localized error block in place of the empty
              // streaming placeholder.
              lastAssistant.errorCode = code;
            }
            break;
          }
        }
      }),

    appendAssistantBubble: bubble =>
      set(s => {
        s.bubbles.push(bubble);
        // Only return to `idle` if there are no more cards still
        // awaiting the user. Multi-card turns commit the first
        // outcome bubble while the sibling cards keep waiting.
        if (s.pendingConfirmations.length === 0) {
          s.status = 'idle';
        }
      }),

    clearPendingConfirmation: confirmationId =>
      set(s => {
        if (confirmationId === undefined) {
          s.pendingConfirmations = [];
          s.busyCards = {};
        } else {
          s.pendingConfirmations = s.pendingConfirmations.filter(
            p => p.id !== confirmationId,
          );
          delete s.busyCards[confirmationId];
        }
        if (s.pendingConfirmations.length === 0) {
          s.isMultiConfirmationStack = false;
        }
        if (
          s.status === 'awaiting_confirmation' &&
          s.pendingConfirmations.length === 0 &&
          !s.confirmationBatch
        ) {
          s.status = 'idle';
        }
      }),

    updatePendingConfirmationPayload: (confirmationId, partial) =>
      set(s => {
        const target = s.pendingConfirmations.find(p => p.id === confirmationId);
        if (!target) return;
        target.payload = {
          ...target.payload,
          ...partial,
        };
      }),

    updatePendingConfirmationPayloadAtPath: (confirmationId, path, value) =>
      set(s => {
        const target = s.pendingConfirmations.find(p => p.id === confirmationId);
        if (!target || path.length === 0) return;
        let cursor: Record<string, unknown> = target.payload;
        for (let i = 0; i < path.length - 1; i++) {
          const key = path[i];
          const nextKey = path[i + 1];
          const existing = cursor[key as string | number];
          if (existing == null || typeof existing !== 'object') {
            cursor[key as string | number] =
              typeof nextKey === 'number' ? [] : {};
          }
          cursor = cursor[key as string | number] as Record<string, unknown>;
        }
        const lastKey = path[path.length - 1];
        cursor[lastKey as string | number] = value as never;
      }),

    setCardBusy: (confirmationId, reason, busy) =>
      set(s => {
        const reasons = s.busyCards[confirmationId] ?? [];
        if (busy === reasons.includes(reason)) return;
        const next = busy
          ? [...reasons, reason]
          : reasons.filter(r => r !== reason);
        // Empty entries are deleted, not kept as `[]`, so "is anything
        // busy?" stays a key count instead of a scan.
        if (next.length > 0) s.busyCards[confirmationId] = next;
        else delete s.busyCards[confirmationId];
      }),

    startConfirmationBatch: input =>
      set(s => {
        s.confirmationBatch = {
          ...input,
          completed: 0,
          results: [],
        };
        s.resolvingConfirmationId = undefined;
      }),

    advanceConfirmationBatch: input =>
      set(s => {
        if (!s.confirmationBatch) return;
        s.confirmationBatch.completed = input.completed;
        s.confirmationBatch.currentConfirmationId = input.currentConfirmationId;
        s.confirmationBatch.currentClientName = input.currentClientName;
        if (input.result) {
          s.confirmationBatch.results.push(input.result);
        }
      }),

    finishConfirmationBatch: () =>
      set(s => {
        s.confirmationBatch = undefined;
        s.resolvingConfirmationId = undefined;
        if (
          s.pendingConfirmations.length === 0 &&
          s.status === 'awaiting_confirmation'
        ) {
          s.status = 'idle';
        }
      }),

    setResolvingConfirmationId: confirmationId =>
      set(s => {
        s.resolvingConfirmationId = confirmationId;
      }),

    updateBubble: (bubbleId, patch) =>
      set(s => {
        const bubble = s.bubbles.find(b => b.id === bubbleId);
        if (!bubble) return;
        Object.assign(bubble, patch);
      }),

    upsertBatchProgressActivity: input =>
      set(s => {
        const bubble = s.bubbles.find(b => b.id === input.bubbleId);
        if (!bubble) return;
        if (!bubble.activities) bubble.activities = [];
        const existing = bubble.activities.find(a => a.id === input.activityId);
        if (existing) {
          existing.status = input.status;
          existing.label = input.label;
          existing.errorMessage = input.errorMessage;
        } else {
          bubble.activities.push({
            id: input.activityId,
            runId: 'batch',
            name: 'create_order',
            status: input.status,
            label: input.label,
            errorMessage: input.errorMessage,
          });
        }
      }),

    reset: () => set(() => ({ ...initialState })),
  })),
);

const chipId = (runId: string, toolCallId: string) =>
  `${runId}:${toolCallId}` || `${runId}:${Math.random().toString(36).slice(2)}`;

const lastAssistantBubble = (bubbles: ChatBubble[]): ChatBubble | undefined => {
  for (let i = bubbles.length - 1; i >= 0; i--) {
    if (bubbles[i].role === 'assistant') return bubbles[i];
  }
  return undefined;
};

const textFromCommittedBlocks = (blocks: unknown[]): string =>
  blocks
    .map(block => {
      if (
        typeof block === 'object' &&
        block !== null &&
        'kind' in block &&
        (block as { kind?: string }).kind === 'text' &&
        'text' in block &&
        typeof (block as { text?: unknown }).text === 'string'
      ) {
        return (block as { text: string }).text;
      }
      return '';
    })
    .filter(Boolean)
    .join('\n');

const mergeWidgetsFromBlocks = (bubble: ChatBubble, blocks: unknown[]): void => {
  for (const block of blocks) {
    if (typeof block !== 'object' || block === null || (block as { kind?: string }).kind !== 'widget') {
      continue;
    }
    const spec = coerceWidgetSpec((block as { widget: unknown }).widget);
    if (!spec || isBubbleSkippedWidgetType(spec.type)) continue;
    const key = widgetDedupeKey(spec);
    const exists = bubble.widgets.some(w => widgetDedupeKey(w) === key);
    if (!exists) bubble.widgets.push(spec);
  }
};

const widgetDedupeKey = (widget: ChatBubble['widgets'][number]): string => {
  switch (widget.type) {
    case 'client_card':
      return `client:${String(widget.clientId)}`;
    case 'order_card':
      return `order:${String(widget.orderId)}`;
    case 'driver_card':
      return `driver:${String(widget.driverId)}`;
    case 'vehicle_card':
      return `vehicle:${String(widget.vehicleId)}`;
    case 'saved_location_card':
      return `saved_location:${String(widget.savedLocationId)}`;
    case 'order_list':
      return `order_list:${widget.orderIds.map(String).join(',')}`;
    default:
      return widget.type;
  }
};
